import React, { useState, useEffect, useRef } from 'react';
import Image from 'gatsby-plugin-sanity-image';
import { StaticImage } from 'gatsby-plugin-image';
import cx from 'classnames';
import { Link } from 'gatsby';
import { motion, AnimatePresence } from 'framer-motion';
import Moment from 'react-moment';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import { BlockWrapper } from '../blockWrapper';
import { PortableTextBlock } from '@modules/portableTextBlock';
import { Arrow } from '@svg/arrow';
import { BackgroundVideo } from '@media/backgroundVideo';
import useInterval from '@hooks/useInterval';

import Vertical from '@images/dso-vertical.png';

const arrowVars = {
  initial: {
    x: -20,
  },
  animate: {
    x: 0,
    transition: {
      duration: 0.3,
    },
  },
};

const Overlays = () => {
  return (
    <>
      <div className='hidden md:block absolute top-0 z-10'>
        <img width={264} height={783} src={Vertical} alt='DSO Logo' />
      </div>

      <div className='md:hidden absolute top-0 z-10'>
        <img width={116} height={344} src={Vertical} alt='DSO Logo' />
      </div>

      {/* <div className="bg-black opacity-30 absolute top-0 left-0 w-full h-full z-10" /> */}
    </>
  );
};

const Pagination = ({
  slides,
  whiteText,
  activeSlide,
  setActiveSlide,
  mobileWhiteText,
  emblaApi,
  //   setIsPlaying,
}) => {
  return (
    <div className='flex relative gap-x-4 mt-10 z-20'>
      {slides.map((x, i) => {
        return (
          <>
            <button
              onClick={() => {
                emblaApi.scrollTo(i);
                // setIsPlaying(false);
              }}
              className={cx('hidden md:block h-2 w-2 rounded-full', {
                'bg-white': whiteText,
                'bg-black': !whiteText,
                'opacity-50': i !== activeSlide,
              })}
            />

            <button
              onClick={() => {
                emblaApi.scrollTo(i);
                // setIsPlaying(false);
              }}
              className={cx('md:hidden h-2 w-2 rounded-full', {
                'bg-white': mobileWhiteText,
                'bg-black': !mobileWhiteText,
                'opacity-50': i !== activeSlide,
              })}
            />
          </>
        );
      })}
    </div>
  );
};

const TextBlock = ({
  activeSlide,
  slides,
  setActiveSlide,
  // setIsPlaying
}) => {
  const { layout, whiteText, mobileWhiteText, text } = slides[activeSlide];
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.5 } }}
      exit={{ opacity: 0, y: 40, transition: { duration: 0.5 } }}
      className={cx('col-start-2 col-end-14 md:absolute w-full mt-8 md:mt-0', {
        'md:col-start-9 md:col-end-14 bottom-28': layout === 'right',
        'md:col-end-7': layout === 'left' || !layout,
      })}>
      {text && (
        <div
          className={cx({
            'max-w-sm mx-auto': layout === 'right',
            'max-w-lg': layout === 'left' || !layout,
          })}>
          <PortableTextBlock
            white={whiteText ? true : false}
            className={cx(
              'hidden md:block text-black prose md:prose-lg md:prose-mb-0 md:prose-mt-0 relative z-20',
              {
                '!text-[#fff]': whiteText,
                'text-black': !whiteText,
              },
            )}
            text={text}
          />

          <PortableTextBlock
            className={cx(
              'md:hidden prose md:prose-lg md:prose-mb-0 md:prose-mt-0 relative z-20',
              {
                'text-black': !mobileWhiteText,
                'text-white': mobileWhiteText,
              },
            )}
            text={text}
          />

          <Pagination
            slides={slides}
            activeSlide={activeSlide}
            setActiveSlide={setActiveSlide}
            whiteText={whiteText}
            mobileWhiteText={mobileWhiteText}
            // setIsPlaying={setIsPlaying}
          />
        </div>
      )}
    </motion.div>
  );
};

const EventBlock = ({
  slides,
  activeSlide,
  setActiveSlide,
  emblaApi,
  //   setIsPlaying,
}) => {
  const { title, session, settings, slug, series } =
    slides[activeSlide].eventRef || {};
  const { layout, whiteText, mobileWhiteText } = slides[activeSlide] || {};

  const [hover, setHover] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.5 } }}
      exit={{ opacity: 0, y: 40, transition: { duration: 0.5 } }}
      className={cx(
        'col-start-2 col-end-14 md:absolute w-full mt-8 md:mt-0 z-20 relative',
        {
          'md:col-start-8 md:col-end-14 md:bottom-28': layout === 'right',
          'md:col-end-8': layout === 'left' || !layout,
        },
      )}>
      <div
        className={cx({
          'mx-auto': layout === 'right',
          'max-w-xl': layout === 'left' || !layout,
        })}>
        <div
          className={cx('hidden md:block text-black', {
            'md:text-white': whiteText,
            'md:text-black': !whiteText,
          })}>
          <span className='font-semibold text-base leading-base'>{series}</span>
          <h1 className='text-5xl leading-4xl font-semibold my-2'>{title}</h1>
          {session.map((s, i) => (
            <span
              key={i}
              className='font-semibold text-base leading-base inline'>
              {i !== 0 && <span className='px-4'>|</span>}
              <Moment withTitle format='ddd DD MMMM YYYY - h:mm A'>
                {s}
              </Moment>
            </span>
          ))}
          <p className='font-medium text-lg leading-lg pt-2 pb-4'>
            {settings.searchExcerpt}
          </p>
          <Link
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            to={`/events/${slug.current}`}
            className='text-lg leading-lg font-semibold flex flex-row justify-between items-center'>
            <span>More Info</span>{' '}
            <motion.span
              variants={arrowVars}
              initial='initial'
              animate={hover ? 'animate' : 'initial'}>
              <Arrow white={whiteText ? true : false} />
            </motion.span>
          </Link>
        </div>

        <div
          className={cx('md:hidden', {
            'md:text-white': whiteText,
            'md:text-black': !whiteText,
            'text-black': !mobileWhiteText,
            'text-white': mobileWhiteText,
          })}>
          {session.map((s, i) => (
            <span className='font-semibold text-sm leading-sm block'>
              <Moment key={i} withTitle format='D MMMM YYYY - h:mm A'>
                {s}
              </Moment>
            </span>
          ))}
          <h1 className='text-4xl leading-4xl font-medium'>{title}</h1>
          <p className='font-medium text-sm leading-sm pt-2 pb-4'>
            {settings.searchExcerpt}
          </p>
          <Link
            to={`/events/${slug.current}`}
            className='text-sm leading-sm font-semibold flex flex-row justify-between items-center font-bold'>
            <span style={{ fontWeight: '700' }}>More Info</span>{' '}
            <Arrow white={mobileWhiteText ? true : false} />
          </Link>
        </div>

        <Pagination
          emblaApi={emblaApi}
          slides={slides}
          activeSlide={activeSlide}
          setActiveSlide={setActiveSlide}
          whiteText={whiteText}
          mobileWhiteText={mobileWhiteText}
          //   setIsPlaying={setIsPlaying}
        />
      </div>
    </motion.div>
  );
};

const Slide = ({
  key,
  activeSlide,
  slides,
  setActiveSlide,
  emblaApi,
  //   setIsPlaying,
}) => {
  return (
    <>
      <motion.div
        className='w-full  h-banner md:h-screen relative col-start-1 col-end-15'
        key={key}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { delay: 0.5 } }}>
        {slides[activeSlide].video ? (
          <BackgroundVideo
            className='w-full h-full'
            video={slides[activeSlide].video}
          />
        ) : (
          slides[activeSlide]?.image?.image && (
            <Image
              {...slides[activeSlide].image.image}
              className='absolute w-full h-full object-cover z-0'
            />
          )
        )}
        {slides[activeSlide].video && (
          <motion.div
            initial={{ opacity: 1 }}
            animate={{ opacity: 0, transition: { delay: 1.5, duration: 1 } }}
            className='absolute top-0 left-0 w-full h-full bg-black'
          />
        )}
        <div className='block w-full h-full bg-[#00000040] absolute top-0 z-10' />
      </motion.div>

      {slides[activeSlide].eventRef ? (
        <EventBlock
          slides={slides}
          activeSlide={activeSlide}
          setActiveSlide={setActiveSlide}
          emblaApi={emblaApi}
          //   setIsPlaying={setIsPlaying}
        />
      ) : slides[activeSlide].text ? (
        <TextBlock
          slides={slides}
          activeSlide={activeSlide}
          setActiveSlide={setActiveSlide}
          //   setIsPlaying={setIsPlaying}
        />
      ) : (
        <div className='flex gap-x-4 mt-10 z-20 absolute bottom-8 left-1/2 transform -translate-x-1/2'>
          {slides.map((x, i) => {
            return (
              <button
                onClick={() => {
                  emblaApi.scrollTo(i);
                  //   setIsPlaying(false);
                }}
                className={cx('h-2 w-2 rounded-full', {
                  'bg-white': i === activeSlide,
                  'bg-grey': i !== activeSlide,
                })}
              />
            );
          })}
        </div>
      )}
    </>
  );
};

const FeatureBanner = ({ slides }) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const autoplay = useRef(
    Autoplay(
      { delay: 10000, stopOnInteraction: false },
      (emblaRoot) => emblaRoot.parentElement,
    ),
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      dragFree: false,
      containScroll: true,
      // startIndex: activeSlide,
    },
    [autoplay.current],
  );

  //   const delay = 10000;
  //   const [isPlaying, setIsPlaying] = useState(true);

  //   useInterval(
  //     () => {
  //       emblaApi.scrollNext();
  //     },
  //     isPlaying ? delay : null
  //   );

  //   useEffect(() => {
  //     setIsPlaying(true);
  //   }, [isPlaying]);

  return (
    <BlockWrapper className='overflow-x-hidden'>
      <div ref={emblaRef} className='w-full'>
        {/* DSO LOGO */}
        <div className='flex w-full h-full'>
          {slides.map((slide, i) => {
            return (
              <div key={i} className='relative w-full'>
                <Overlays />
                {/* Slides */}
                <div className='w-screen relative grid grid-cols-14 items-center'>
                  <AnimatePresence exitBeforeEnter>
                    <Slide
                      key={activeSlide}
                      slides={slides}
                      activeSlide={i}
                      setActiveSlide={setActiveSlide}
                      emblaApi={emblaApi}
                      //   setIsPlaying={setIsPlaying}
                    />
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </BlockWrapper>
  );
};

export default FeatureBanner;
